// ============================================================================
// MONITORING CONSTANTS
// ============================================================================

import React from "react";

/**
 * Supported monitoring types in the application
 */
export const MONITORING_TYPES = {
  KEYWORD: "keyword",
  INFLUENCER: "influencer",
  SOURCE: "source",
  PULSE: "pulse",
} as const;

export type MonitoringType =
  (typeof MONITORING_TYPES)[keyof typeof MONITORING_TYPES];

/**
 * Tailwind background color classes for monitoring type badges
 */
export const MONITORING_BADGE_STYLES: Record<MonitoringType, string> = {
  [MONITORING_TYPES.KEYWORD]: "bg-monitor-keyword",
  [MONITORING_TYPES.INFLUENCER]: "bg-monitor-influencer",
  [MONITORING_TYPES.SOURCE]: "bg-monitor-source",
  [MONITORING_TYPES.PULSE]: "bg-monitor-pulse",
};

/**
 * Configuration interface for monitoring modules
 */
export interface MonitoringConfig {
  readonly type: MonitoringType;
  readonly label: string;
  readonly icon: string; // RemixIcon class name
  readonly path: string;
  readonly description: string;
}

/**
 * Centralized monitoring configurations
 * Single source of truth for all monitoring module metadata
 */
export const MONITORING_CONFIGS: Record<MonitoringType, MonitoringConfig> = {
  [MONITORING_TYPES.KEYWORD]: {
    type: MONITORING_TYPES.KEYWORD,
    label: "Keyword Monitoring",
    icon: "ri-hashtag",
    path: "/keyword-monitoring",
    description: "Monitor keywords across social media platforms",
  },
  [MONITORING_TYPES.INFLUENCER]: {
    type: MONITORING_TYPES.INFLUENCER,
    label: "Influencer Monitoring",
    icon: "ri-speak-line",
    path: "/influencer-monitoring",
    description: "Track influencer activities and engagement",
  },
  [MONITORING_TYPES.SOURCE]: {
    type: MONITORING_TYPES.SOURCE,
    label: "Source Monitoring",
    icon: "ri-article-line",
    path: "/source-monitoring",
    description: "Monitor content from specific sources",
  },
  [MONITORING_TYPES.PULSE]: {
    type: MONITORING_TYPES.PULSE,
    label: "Pulse Monitoring",
    icon: "ri-radar-line",
    path: "/pulse",
    description: "Real-time pulse and trend monitoring",
  },
  // [MONITORING_TYPES.POST_SEARCH]: {
  //   type: MONITORING_TYPES.POST_SEARCH,
  //   label: "Post Search",
  //   icon: "ri-search-line",
  //   path: "/post-search",
  //   description: "Search and analyze specific posts",
  // },
} as const;

/** Valid plan segment labels for client cards */
export const PLAN_SEGMENT_LABELS = [
  "Inactive",
  "Starter",
  "Basic",
  "Plus",
  "Premium",
  "Enterprise",
  "Usage",
] as const;

export type PlanSegmentLabel = (typeof PLAN_SEGMENT_LABELS)[number];

/** Icon for non-monitoring segments (e.g. Inactive) */
export const PLAN_SEGMENT_INACTIVE_ICON = "ri-close-circle-line";

/**
 * Get monitoring type from plan segment text (e.g. "Keyword Monitoring", "Pulse")
 */
export function getMonitoringTypeFromPlanSegment(
  segment: string,
): MonitoringType {
  const n = segment.toLowerCase().trim();
  if (n.includes("influencer")) return MONITORING_TYPES.INFLUENCER;
  if (n.includes("source") || n.includes("usage"))
    return MONITORING_TYPES.SOURCE;
  if (n.includes("pulse")) return MONITORING_TYPES.PULSE;
  if (n.includes("keyword")) return MONITORING_TYPES.KEYWORD;
  return MONITORING_TYPES.KEYWORD;
}

export interface PlanSegmentParsed {
  label: PlanSegmentLabel;
  icon: string;
}

/**
 * Parse plan string into validated segments with labels and monitoring-based icons.
 * Segments are one of: Inactive, Starter, Basic, Plus, Premium, Enterprise, Usage.
 */
export function parsePlanSegments(
  plan: string | null | undefined,
): PlanSegmentParsed[] {
  if (!plan || typeof plan !== "string") return [];
  const raw = plan
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const result: PlanSegmentParsed[] = [];
  const seen = new Set<string>();

  for (const segment of raw) {
    const n = segment.toLowerCase();
    let label: PlanSegmentLabel;
    let icon: string;

    if (n === "inactive") {
      label = "Inactive";
      icon = PLAN_SEGMENT_INACTIVE_ICON;
    } else if (
      n === "usage" ||
      (n.includes("usage") &&
        (n.includes("source") || n.includes("usage based")))
    ) {
      label = "Usage";
      icon = MONITORING_CONFIGS[MONITORING_TYPES.SOURCE].icon;
    } else {
      const dash = segment.indexOf(" - ");
      const tierPart = dash >= 0 ? segment.slice(0, dash).trim() : segment;
      const monitoringPart = dash >= 0 ? segment.slice(dash + 3).trim() : "";
      const tierLower = tierPart.toLowerCase();
      if (tierLower === "starter") label = "Starter";
      else if (tierLower === "basic") label = "Basic";
      else if (tierLower === "plus") label = "Plus";
      else if (tierLower === "premium") label = "Premium";
      else if (tierLower === "enterprise") label = "Enterprise";
      else continue;
      const monType = getMonitoringTypeFromPlanSegment(
        monitoringPart || tierPart,
      );
      icon = MONITORING_CONFIGS[monType].icon;
    }

    const key = `${label}:${icon}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ label, icon });
  }

  return result;
}

/**
 * Type guard to validate monitoring type
 */
export function isValidMonitoringType(type: string): type is MonitoringType {
  return Object.values(MONITORING_TYPES).includes(type as MonitoringType);
}

/**
 * Get monitoring configuration by type with runtime validation
 * @param type - The monitoring type
 * @returns The monitoring configuration
 * @throws Error if type is invalid
 */
export function getMonitoringConfig(type: MonitoringType): MonitoringConfig {
  if (!isValidMonitoringType(type)) {
    throw new Error(`Invalid monitoring type: ${type}`);
  }
  return MONITORING_CONFIGS[type];
}

/**
 * Create monitoring icon React element with consistent styling
 * @param type - The monitoring type
 * @param className - Additional CSS classes
 * @param isDarkMode - Whether dark mode is active
 * @returns React icon element
 */
export function createMonitoringIcon(
  type: MonitoringType,
  className: string = "text-[20px]",
  isDarkMode: boolean = false,
): React.ReactElement {
  const config = getMonitoringConfig(type);
  const baseTextColor = isDarkMode ? "text-white" : "text-neutral-950";
  const iconClassName = `${config.icon} ${className} ${baseTextColor}`;

  return React.createElement("i", {
    className: iconClassName,
    key: `monitoring-icon-${type}`,
  });
}

/**
 * Determine monitoring type from token string with fallback
 * @param token - The token string to analyze
 * @returns The determined monitoring type
 */
export function getMonitoringTypeFromToken(token: string): MonitoringType {
  if (!token || typeof token !== "string") {
    return MONITORING_TYPES.KEYWORD; // Safe fallback
  }

  const normalizedToken = token.toLowerCase().trim();

  // Check for specific keywords in token
  if (normalizedToken.includes("inf")) {
    return MONITORING_TYPES.INFLUENCER;
  }
  if (normalizedToken.includes("sm")) {
    return MONITORING_TYPES.SOURCE;
  }
  if (normalizedToken.includes("pulse")) {
    return MONITORING_TYPES.PULSE;
  }
  if (normalizedToken.includes("km")) {
    return MONITORING_TYPES.KEYWORD;
  }
  if (normalizedToken.includes("ps")) {
    return MONITORING_TYPES.KEYWORD;
  }
  if (normalizedToken.includes("premium")) {
    return MONITORING_TYPES.KEYWORD;
  }
  if (normalizedToken.includes("enterprise")) {
    return MONITORING_TYPES.KEYWORD;
  }

  // Default fallback
  return MONITORING_TYPES.KEYWORD;
}

/**
 * Get all available monitoring types
 * @returns Array of all monitoring types
 */
export function getAllMonitoringTypes(): MonitoringType[] {
  return Object.values(MONITORING_TYPES);
}

/**
 * Get all monitoring configurations
 * @returns Array of all monitoring configurations
 */
export function getAllMonitoringConfigs(): MonitoringConfig[] {
  return Object.values(MONITORING_CONFIGS);
}

/**
 * Determine monitoring type from API endpoint
 * @param endpoint - The API endpoint path
 * @returns The determined monitoring type
 */
export function getMonitoringTypeFromEndpoint(
  endpoint: string,
): MonitoringType {
  if (!endpoint || typeof endpoint !== "string") {
    return MONITORING_TYPES.KEYWORD; // Safe fallback
  }

  const normalizedEndpoint = endpoint.toLowerCase().trim();

  // Influencer endpoints
  if (
    normalizedEndpoint.includes("/influencer") ||
    normalizedEndpoint.includes("/influencers")
  ) {
    return MONITORING_TYPES.INFLUENCER;
  }

  // Source/Media endpoints (print, online, tv, radio, quadmedia)
  if (
    normalizedEndpoint.includes("/sources") ||
    normalizedEndpoint.includes("/quadmedia") ||
    normalizedEndpoint.includes("/print/") ||
    normalizedEndpoint.includes("/online") ||
    normalizedEndpoint.includes("/video/") ||
    normalizedEndpoint.includes("/radio") ||
    normalizedEndpoint.includes("/tv") ||
    normalizedEndpoint.includes("/blogs")
  ) {
    return MONITORING_TYPES.SOURCE;
  }

  // Pulse endpoints
  if (normalizedEndpoint.includes("/pulse")) {
    return MONITORING_TYPES.PULSE;
  }

  // Keyword/Posts endpoints (default for most content endpoints)
  if (
    normalizedEndpoint.includes("/posts") ||
    normalizedEndpoint.includes("/es/posts") ||
    normalizedEndpoint.includes("/keywords") ||
    normalizedEndpoint.includes("/search")
  ) {
    return MONITORING_TYPES.KEYWORD;
  }

  // Default fallback - log unmatched endpoints for debugging
  console.warn(
    `Unmatched endpoint pattern: ${endpoint}, defaulting to KEYWORD`,
  );
  return MONITORING_TYPES.KEYWORD;
}
